import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import Nav from "./Nav";
import Swal from "sweetalert2";
import moment from "moment";
import Spinner from "./Spinner";
import Sidebar from "./Sidebar";
import { useNavContext } from "../context/NavContext";
import { orgCollection, orgDocIn } from "../utils/firePaths";
import { useAuth } from "../context/AuthContext";
import Breadcrumb from "./Breadcrumb";
import ContactDetailModal from "./ContactDetailModal";
import ContactFormModal from "./ContactFormModal";
import TableToolbar from "./TableToolbar";
import DataTable from "./DataTable";
import TablePagination from "./TablePagination";

moment.locale("es");

const parseFecha = (fecha) => {
  if (!fecha) return null;
  if (fecha.seconds) return new Date(fecha.seconds * 1000);
  if (fecha.toDate) return fecha.toDate();
  const d = new Date(fecha);
  return isNaN(d.getTime()) ? null : d;
};

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "650px",
    borderRadius: "15px",
    padding: "0",
    border: "none",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    overflow: "hidden",
    fontFamily: "'Inter', sans-serif",
  },
  overlay: { backgroundColor: "rgba(0, 0, 0, 0.4)", zIndex: 1009 },
};

const viewModalStyles = {
  ...customStyles,
  content: {
    ...customStyles.content,
    maxWidth: "850px",
  },
};

const ListaClientes = () => {
  const { user, orgId } = useAuth();
  const { proximasAVencer, proximosEventos, searchTerm, setSearchTerm } =
    useNavContext();
  const [clientes, setClientes] = useState([]);
  const [formData, setFormData] = useState({
    empresa: "",
    rfc: "",
    telefono: "",
    correo: "",
    domicilio: "",
    numeroRegistro: "",
    contactoPersonal: "",
  });
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [viewModalIsOpen, setViewModalIsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "numeroRegistro",
    direction: "asc",
  });
  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInColumnMenu = e.target.closest(
        ".dropdown-custom.column-config",
      );
      const clickedInColumnBtn = e.target.closest('[data-dd="columns"]');

      const clickedInExportMenu = e.target.closest(
        ".dropdown-custom.export-menu",
      );
      const clickedInExportBtn = e.target.closest('[data-dd="export"]');

      if (showColumnConfig && !clickedInColumnMenu && !clickedInColumnBtn) {
        setShowColumnConfig(false);
      }

      if (showExportDropdown && !clickedInExportMenu && !clickedInExportBtn) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [showColumnConfig, showExportDropdown]);

  const handleViewClient = (cliente) => {
    setSelectedClient(cliente);
    setViewModalIsOpen(true);
  };

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const guardado = localStorage.getItem("columnConfig_clientes");
    const defaultConfig = {
      empresa: true,
      correo: true,
      telefono: true,
      fechaRegistro: true,
    };
    return guardado ? JSON.parse(guardado) : defaultConfig;
  });

  const columnOptionsClientes = useMemo(
    () => [
      { key: "empresa", label: "Razón Social" },
      { key: "correo", label: "Correo" },
      { key: "telefono", label: "Teléfono" },
      { key: "fechaRegistro", label: "Fecha de Registro" },
    ],
    [],
  );

  const handleEditSelected = () => {
    if (selectedIds.length === 1) {
      const clienteParaEditar = clientes.find((i) => i.id === selectedIds[0]);
      if (clienteParaEditar) {
        setFormData(clienteParaEditar);
        setEditingClientId(clienteParaEditar.id);
        setModalIsOpen(true);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (!user?.uid || !orgId) {
      Swal.fire({
        title: "Error",
        text: "Sesión no válida. Intenta recargar la página.",
        icon: "error",
        customClass: { popup: "swal-popup-reset" },
      });
      return;
    }

    if (selectedIds.length === 0) return;

    const result = await Swal.fire({
      html: `
      <div class="custom-modal-container">
        <div class="custom-modal-header">
          <div class="custom-modal-icon-box">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" x2="12" y1="8" y2="12"></line><line x1="12" x2="12.01" y1="16" y2="16"></line></svg>
          </div>
          <h3 class="custom-modal-title">Eliminar Clientes</h3>
        </div>
        <p class="custom-modal-description">¿Estás seguro de que deseas eliminar ${selectedIds.length} cliente(s)?</p>
        <div class="custom-modal-actions">
          <button id="cancel-btn" class="btn-modal-cancel">Cancelar</button>
          <button id="confirm-btn" class="btn-modal-delete">Eliminar</button>
        </div>
      </div>
    `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      allowOutsideClick: false,
      customClass: {
        popup: "swal-popup-reset",
        htmlContainer: "swal-html-reset",
      },
      didOpen: () => {
        document
          .getElementById("cancel-btn")
          .addEventListener("click", () => Swal.close());
        document
          .getElementById("confirm-btn")
          .addEventListener("click", () => Swal.clickConfirm());
      },
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        selectedIds.map(async (id) => {
          await deleteDoc(orgDocIn(orgId, "clientes", id));
        }),
      );
      setSelectedIds([]);
      Swal.fire({
        icon: "success",
        title: "Completado",
        text: "Los clientes fueron eliminados correctamente.",
        timer: 1500,
        showConfirmButton: false,
        customClass: { popup: "swal-popup-reset" },
      });
    } catch (error) {
      console.error("Error al eliminar clientes:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `No se pudo eliminar: ${error.message}`,
        customClass: { popup: "swal-popup-reset" },
      });
    }
  };

  useEffect(() => {
    if (!user?.uid || !orgId) return;

    setIsLoading(true);

    const unsub = onSnapshot(orgCollection(orgId, "clientes"), (snap) => {
      setClientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setIsLoading(false);
    });

    return () => unsub();
  }, [user?.uid, orgId]);

  const resetFormData = useCallback(() => {
    setFormData({
      empresa: "",
      rfc: "",
      telefono: "",
      correo: "",
      domicilio: "",
      numeroRegistro: "",
      contactoPersonal: "",
    });
    setEditingClientId(null);
  }, []);

  const handleOpenModal = () => {
    resetFormData();
    setModalIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.uid || !orgId) {
      return Swal.fire("Error", "Sesión no válida.", "error");
    }

    if (!formData.empresa.trim()) {
      return Swal.fire("Error", "La Razón Social es obligatoria", "error");
    }

    const colRef = orgCollection(orgId, "clientes");

    const datosAGuardar = {
      ...formData,
      tipo: "Cliente",
    };

    // solo en creación
    if (!editingClientId) {
      datosAGuardar.fechaRegistro = serverTimestamp();
    }

    // limpiar undefined
    Object.keys(datosAGuardar).forEach(
      (k) => datosAGuardar[k] === undefined && delete datosAGuardar[k],
    );

    try {
      if (editingClientId) {
        await updateDoc(orgDocIn(orgId, "clientes", editingClientId), {
          ...datosAGuardar,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(colRef, {
          ...datosAGuardar,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      setSelectedIds([]);
      setModalIsOpen(false);
      setEditingClientId(null);

      Swal.fire({
        icon: "success",
        title: "Guardado",
        timer: 1000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error al guardar cliente:", err);
      Swal.fire("Error", "No se pudieron guardar los cambios", "error");
    }
  };

  const handleAutoAsignar = () => {
    let maxNum = 0;
    clientes.forEach((c) => {
      if (c.numeroRegistro?.startsWith("C-")) {
        const parts = c.numeroRegistro.split("-");
        const n = parseInt(parts[1]);
        if (!isNaN(n) && n > maxNum) maxNum = n;
      }
    });
    setFormData((prev) => ({
      ...prev,
      numeroRegistro: `C-${String(maxNum + 1).padStart(4, "0")}`,
    }));
  };

  const filteredSortedData = useMemo(() => {
    const s = (searchTerm || "").toLowerCase();

    let result = clientes.filter((c) => {
      const empresa = (c.empresa || "").toLowerCase();
      const rfc = (c.rfc || "").toLowerCase();
      const correo = (c.correo || "").toLowerCase();
      const telefono = (c.telefono || "").toString().toLowerCase();
      const numeroRegistro = (c.numeroRegistro || "").toLowerCase();

      const matchSearch =
        empresa.includes(s) ||
        rfc.includes(s) ||
        correo.includes(s) ||
        telefono.includes(s) ||
        numeroRegistro.includes(s);

      return matchSearch;
    });

    const getNumRegistro = (str) => {
      if (!str) return 0;
      const parts = String(str).split("-");
      return parts.length > 1 ? parseInt(parts[1], 10) : 0;
    };

    result.sort((a, b) => {
      const key = sortConfig.key;

      // fechaRegistro
      if (key === "fechaRegistro") {
        const fa = parseFecha(a.fechaRegistro);
        const fb = parseFecha(b.fechaRegistro);
        const A = fa ? fa.getTime() : 0;
        const B = fb ? fb.getTime() : 0;
        return sortConfig.direction === "asc" ? A - B : B - A;
      }

      // numeroRegistro (C-0001)
      if (key === "numeroRegistro") {
        const A = getNumRegistro(a.numeroRegistro);
        const B = getNumRegistro(b.numeroRegistro);
        return sortConfig.direction === "asc" ? A - B : B - A;
      }

      // texto
      const A = (a[key] ?? "").toString().toLowerCase();
      const B = (b[key] ?? "").toString().toLowerCase();
      return sortConfig.direction === "asc"
        ? A.localeCompare(B)
        : B.localeCompare(A);
    });

    return result;
  }, [clientes, searchTerm, sortConfig]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSortedData.length / itemsPerPage),
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const currentItems = useMemo(() => {
    return filteredSortedData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );
  }, [filteredSortedData, currentPage, itemsPerPage]);

  const columnsClientes = useMemo(() => {
    return [
      {
        id: "empresa",
        label: "Razón Social",
        visible: visibleColumns.empresa,
        sortKey: "empresa",
        cellClassName: "table-td col-left",
        render: (c) => c.empresa || "---",
      },
      {
        id: "correo",
        label: "Correo",
        visible: visibleColumns.correo,
        sortKey: "correo",
        cellClassName: "table-td col-center",
        render: (c) => c.correo || "---",
      },
      {
        id: "telefono",
        label: "Teléfono",
        visible: visibleColumns.telefono,
        sortKey: "telefono",
        cellClassName: "table-td col-center",
        render: (c) => c.telefono || "---",
      },
      {
        id: "fechaRegistro",
        label: "Fecha de Registro",
        visible: visibleColumns.fechaRegistro,
        sortKey: "fechaRegistro",
        cellClassName: "table-td col-center",
        render: (c) => {
          const fecha = parseFecha(c.fechaRegistro);
          return fecha ? moment(fecha).format("DD/MM/YYYY") : "Sin fecha";
        },
      },
    ];
  }, [visibleColumns]);

  const toggleSelectAll = () => {
    const ids = currentItems.map((i) => i.id);
    const allSelected =
      ids.length > 0 && ids.every((id) => selectedIds.includes(id));

    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((x) => !ids.includes(x))
        : [...new Set([...prev, ...ids])],
    );
  };

  return (
    <>
      <div className="no-sticky">
        <Nav
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />
        <Sidebar />

        <div className="main-content-wrapper">
          <div style={{ marginBottom: "25px" }}>
            <Breadcrumb />
            <h1 className="page-title">Clientes</h1>
          </div>

          <TableToolbar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchPlaceholder="Buscar contactos..."
            showColumnConfig={showColumnConfig}
            setShowColumnConfig={setShowColumnConfig}
            showExportDropdown={showExportDropdown}
            setShowExportDropdown={setShowExportDropdown}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            columnOptions={columnOptionsClientes}
            onAdd={handleOpenModal}
            addButtonTooltip="Nuevo"
            fileInputRef={null}
            onExport={() => {
              setShowExportDropdown(false);
            }}
          />

          {selectedIds.length > 0 && (
            <div className="selection-bar">
              <span>
                {selectedIds.length === 1
                  ? "1 seleccionado"
                  : `${selectedIds.length} seleccionado(s)`}
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="btn-selection btn-edit"
                  style={{ opacity: selectedIds.length > 1 ? 0.5 : 1 }}
                  onClick={handleEditSelected}
                  disabled={selectedIds.length > 1}
                >
                  <i className="bi bi-pencil-square" /> <span>Editar</span>
                </button>
                <button
                  className="btn-selection btn-delete"
                  onClick={handleDeleteSelected}
                >
                  <i className="bi bi-trash" /> <span>Eliminar</span>
                </button>
              </div>
            </div>
          )}

          <DataTable
            loading={isLoading}
            loadingFallback={
              <div style={{ padding: "50px" }}>
                <Spinner />
              </div>
            }
            rows={currentItems}
            columns={columnsClientes}
            selectedIds={selectedIds}
            onToggleAll={toggleSelectAll}
            onToggleRow={(id) =>
              setSelectedIds((prev) =>
                prev.includes(id)
                  ? prev.filter((x) => x !== id)
                  : [...prev, id],
              )
            }
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={(row) => handleViewClient(row)}
            emptyTitle="Aún no hay clientes."
            emptySubtitle="Crea un cliente para comenzar."
            emptyIcon={
              <img
                src="/img/no-data.svg"
                alt="Sin datos"
                style={{ width: 220, maxWidth: "70%", opacity: 0.95 }}
              />
            }
            footer={
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(p) => setCurrentPage(p)}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={(n) => {
                  setItemsPerPage(n);
                  setCurrentPage(1);
                }}
                itemsPerPageOptions={[5, 10, 20]}
                selectedCount={selectedIds.length}
                selectedLabelSingular="cliente"
                selectedLabelPlural="clientes"
                itemsLabel="Mostrando:"
              />
            }
          />
        </div>
      </div>

      <ContactFormModal
        isOpen={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        handleAutoAsignar={handleAutoAsignar}
        isEditing={!!editingClientId}
        hideTypeSelector={true}
        title={editingClientId ? "Editar Cliente" : "Agregar Cliente"}
        defaultType="Cliente"
      />

      <ContactDetailModal
        isOpen={viewModalIsOpen}
        onClose={() => setViewModalIsOpen(false)}
        contact={selectedClient}
        modalStyles={viewModalStyles}
      />
    </>
  );
};

export default ListaClientes;
